import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import VendorMarketplace from "./pages/VendorMarketplace";
import VendorDetail from "./pages/VendorDetail";
import ChoiceMobile from "./pages/ChoiceMobile";
import ShuttleService from "./pages/ShuttleService";
import AuthPage from "./pages/AuthPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import VendorDashboard from "./pages/VendorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import BookingFlow from "./pages/BookingFlow";
import ContractSign from "./pages/ContractSign";
import AuthCallback from "./pages/AuthCallback";
import VendorApplication from "./pages/VendorApplication";
import VendorBenefits from "./pages/VendorBenefits";

import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults for session handling
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    config.headers['X-Session-Token'] = sessionToken;
  }
  return config;
});

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/auth" replace />;
  
  switch (user.role) {
    case "admin":
      return <AdminDashboard />;
    case "vendor":
      return <VendorDashboard />;
    default:
      return <CustomerDashboard />;
  }
};

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (from Google OAuth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/vendors" element={<VendorMarketplace />} />
      <Route path="/vendors/:vendorId" element={<VendorDetail />} />
      <Route path="/choice-mobile" element={<ChoiceMobile />} />
      <Route path="/shuttle" element={<ShuttleService />} />
      <Route path="/become-vendor" element={<VendorBenefits />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route 
        path="/vendor-application" 
        element={
          <ProtectedRoute>
            <VendorApplication />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/booking/:vendorId" 
        element={
          <ProtectedRoute>
            <BookingFlow />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contract/:contractId" 
        element={
          <ProtectedRoute>
            <ContractSign />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Axios interceptor will automatically add X-Session-Token header
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Clear any stale session token on auth failure
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Store session token for browsers blocking cookies
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, role = "customer") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    // Store session token as backup for browsers blocking cookies
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setAuthUser, checkAuth }}>
      <div className="App min-h-screen bg-obsidian">
        {/* Noise overlay for texture */}
        <div className="noise-overlay"></div>
        
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: '#F5F5F5',
            },
          }}
        />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
